import React, { useEffect, useState } from "react";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import { MdCheckBoxOutlineBlank, MdCheckBox, MdDelete } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { RiSave3Fill } from "react-icons/ri";
import { ImCancelCircle } from "react-icons/im";
import { FaPlusSquare } from "react-icons/fa";
import NewEntityModal from "@/elements/modals/NewEntityModal";
import ConfirmModal from "@/elements/modals/ConfirmModal";
import { toast } from "react-toastify";

export default function AdminRecentsColumnsTab(props: any) {
  const [data, updateData] = useState<any>({});
  const [historyColumnSets, setHistoryColumnSets] = useState<any[]>([]);
  const [selectedColumnSet, setSelectedColumnSet] = useState<any>(null);
  const [localCheckedColumns, setLocalCheckedColumns] = useState<Set<number>>(new Set());
  const [localColumnOrder, setLocalColumnOrder] = useState<Map<number, number>>(new Map());
  const [localColumnNames, setLocalColumnNames] = useState<Map<number, string>>(new Map());
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalColumnSetState, setOriginalColumnSetState] = useState(null);
  const [pendingColumnSet, setPendingColumnSet] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showNewColumnSetModal, setShowNewColumnSetModal] = useState(false);
  const [historyEditingId, updateHistoryEditingId] = useState(0);
  const [historyColumnTitle, updateHistoryColumnTitle] = useState("");
  const [columnSetEditingId, setColumnSetEditingId] = useState(0);
  const [columnSetEditingName, setColumnSetEditingName] = useState("");
  const [draggedRowId, setDraggedRowId] = useState(null);
  const [dropTargetRowId, setDropTargetRowId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);

  async function getAdminColumnsAsync() {
    const resp = await RightTimeApi.getAsync("/AdmColumns");
    if (resp && resp.ok && resp.jsonData) {
      updateData(resp.jsonData);
    }
  }

  async function getHistoryColumnSets() {
    const resp = await RightTimeApi.getAsync("/HistoryColumnSets");
    if (resp && resp.ok && resp.jsonData) {
      setHistoryColumnSets(resp.jsonData);
    }
  }

  function openNewColumnSetModal() {
    setShowNewColumnSetModal(true);
  }

  function closeNewColumnSetModal() {
    setShowNewColumnSetModal(false);
  }

  async function createNewColumnSet(name) {
    if (name && name.trim()) {
      const postData = { name: name.trim() };
      const resp = await RightTimeApi.postAsync("/HistoryColumnSet", postData);
      if (resp && resp.ok) {
        await getHistoryColumnSets();
        // Find and select the newly created column set
        const updatedSets = await RightTimeApi.getAsync("/HistoryColumnSets");
        if (updatedSets && updatedSets.ok && updatedSets.jsonData) {
          const newColumnSet = updatedSets.jsonData.find(set => set.name === name.trim());
          if (newColumnSet) {
            exitEditMode(); // Exit any existing edit mode
            setSelectedColumnSet(newColumnSet);
            enterEditMode(newColumnSet);
          }
        }
      }
    }
  }

  async function deleteColumnSet(id) {
    const resp = await RightTimeApi.deleteAsync(`/HistoryColumnSet/${id}`);
    if (resp && resp.ok) {
      // Clear selection if the deleted set was selected
      if (selectedColumnSet?.id === id) {
        setSelectedColumnSet(null);
      }
      await getHistoryColumnSets();
    }
  }

  function selectColumnSet(columnSet) {
    if (isEditMode) {
      // Check if there are any changes before showing confirmation
      if (hasChanges()) {
        setPendingColumnSet(columnSet);
        setShowSaveConfirm(true);
      } else {
        // No changes, switch directly
        exitEditMode();
        setSelectedColumnSet(columnSet);
        enterEditMode(columnSet);
      }
    } else {
      setSelectedColumnSet(columnSet);
      enterEditMode(columnSet);
    }
  }

  function hasChanges() {
    if (!selectedColumnSet || !originalColumnSetState) return false;
    
    // Get current local checked columns
    const currentCheckedColumns = Array.from(localCheckedColumns);
    const originalColumns = originalColumnSetState.selectedColumns || [];
    
    if (currentCheckedColumns.length !== originalColumns.length) {
      return true;
    }
    
    // Check if any columns were added/removed
    if (!currentCheckedColumns.every(id => originalColumns.includes(id))) {
      return true;
    }

    const originalOrder = (selectedColumnSet.columns || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((col) => col.historyColumn.id);
    const currentOrder = currentCheckedColumns
      .slice()
      .sort((a, b) => (localColumnOrder.get(a) || 0) - (localColumnOrder.get(b) || 0));
    if (originalOrder.some((id, index) => id !== currentOrder[index])) {
      return true;
    }
    
    // Check if any column names were changed
    for (const [columnId, newName] of localColumnNames) {
      const originalColumn = selectedColumnSet?.columns?.find(col => col.historyColumn.id === columnId);
      const originalName = originalColumn?.columnName || originalColumn?.historyColumn?.name || '';
      if (newName !== originalName) {
        return true;
      }
    }
    
    return false;
  }

  function enterEditMode(columnSet) {
    setIsEditMode(true);
    
    // Initialize local checked columns, their order, and their names
    const { checkedColumns: currentChecked, columnOrder: currentOrder, columnNames: currentNames } =
      buildLocalStateFromColumnSet(columnSet);
    setLocalCheckedColumns(currentChecked);
    setLocalColumnOrder(currentOrder);
    setLocalColumnNames(currentNames);
    
    // Store the original state for comparison
    setOriginalColumnSetState({
      selectedColumns: Array.from(currentChecked)
    });
  }

  function exitEditMode() {
    setIsEditMode(false);
    setOriginalColumnSetState(null);
    setLocalCheckedColumns(new Set());
    setLocalColumnOrder(new Map());
    setLocalColumnNames(new Map());
  }

  function handleSaveConfirm() {
    saveColumnSetChanges();
    setSelectedColumnSet(pendingColumnSet);
    enterEditMode(pendingColumnSet);
    setShowSaveConfirm(false);
    setPendingColumnSet(null);
  }

  function handleCancelSave() {
    setShowSaveConfirm(false);
    setPendingColumnSet(null);
  }

  async function saveColumnSetChanges() {
    if (!selectedColumnSet || !isEditMode) return;
    
    // Get columns from local checked state and sort by their local order
    const checkedColumnIds = Array.from(localCheckedColumns)
      .sort((a, b) => (localColumnOrder.get(a) || 0) - (localColumnOrder.get(b) || 0));
    
    const checkedColumns = checkedColumnIds.map(id => 
      data.historyColumns?.find(hc => hc.id === id)
    ).filter(Boolean);
    
    // Build the column set data with proper ordering and names
    const columnSetData = {
      id: selectedColumnSet.id,
      name: selectedColumnSet.name,
      columns: checkedColumns.map((hc, index) => ({
        historyColumnId: hc.id,
        order: (index + 1) * 10,
        columnName: localColumnNames.get(hc.id) || hc.name
      }))
    };
    
    const resp = await RightTimeApi.putAsync(`/HistoryColumnSet/${selectedColumnSet.id}`, columnSetData);
    if (resp && resp.ok) {
      await getHistoryColumnSets();
      // Find the updated column set and select it
      const updatedSets = await RightTimeApi.getAsync("/HistoryColumnSets");
      if (updatedSets && updatedSets.ok && updatedSets.jsonData) {
        const updatedColumnSet = updatedSets.jsonData.find(set => set.id === selectedColumnSet.id);
        if (updatedColumnSet) {
          setSelectedColumnSet(updatedColumnSet);
        }
      }
      exitEditMode();
      toast.success("Column set saved successfully");
    }
  }

  function startHistoryEdit(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    updateHistoryEditingId(id);
    
    // Get the current name from local state or fallback to original data
    const currentName = localColumnNames.get(id) || 
      selectedColumnSet?.columns?.find((col) => col.historyColumn.id === id)?.columnName ||
      data.historyColumns.find((hc) => hc.id === id)?.name || '';
      
    updateHistoryColumnTitle(currentName);
    
    // Enter edit mode if a column set is selected and we're not already in edit mode
    if (selectedColumnSet && !isEditMode) {
      enterEditMode(selectedColumnSet);
    }
  }

  function handleDragStart(e, rowId) {
    setDraggedRowId(rowId);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e, rowId) {
    e.preventDefault(); // Allow dropping
    const boundingRect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - boundingRect.top;

    // Determine if the drop is above or below the row
    if (offsetY < boundingRect.height / 2) {
      setDropPosition("above");
    } else {
      setDropPosition("below");
    }
    setDropTargetRowId(rowId);
  }

  function handleDrop(e) {
    e.preventDefault();
    if (draggedRowId !== null && dropTargetRowId !== null && isEditMode && selectedColumnSet) {
      reorderColumns(draggedRowId, dropTargetRowId, dropPosition);
    }
    setDraggedRowId(null);
    setDropTargetRowId(null);
    setDropPosition(null);
  }

  function handleDragEnd() {
    setDraggedRowId(null);
    setDropTargetRowId(null);
    setDropPosition(null);
  }

  function reorderColumns(draggedColumnId, targetColumnId, position) {
    if (!localCheckedColumns.has(draggedColumnId) || !localCheckedColumns.has(targetColumnId)) {
      return; // Only allow reordering of checked columns
    }

    const newColumnOrder = new Map(localColumnOrder);
    
    // Get all checked columns sorted by current order
    const orderedColumns = Array.from(localCheckedColumns)
      .sort((a, b) => (newColumnOrder.get(a) || 0) - (newColumnOrder.get(b) || 0));
    
    // Find the indices
    const draggedIndex = orderedColumns.indexOf(draggedColumnId);
    const targetIndex = orderedColumns.indexOf(targetColumnId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Remove the dragged item
    const reorderedColumns = [...orderedColumns];
    reorderedColumns.splice(draggedIndex, 1);
    
    // Insert at new position
    let insertIndex = targetIndex;
    if (draggedIndex < targetIndex) {
      insertIndex = targetIndex - 1; // Adjust for removed item
    }
    
    if (position === "below") {
      insertIndex += 1;
    }
    
    reorderedColumns.splice(insertIndex, 0, draggedColumnId);
    
    // Update the order map with new positions
    reorderedColumns.forEach((columnId, index) => {
      newColumnOrder.set(columnId, index + 1);
    });
    
    setLocalColumnOrder(newColumnOrder);
  }

  function saveHistoryEditAsync(e) {
    // Store the name change locally instead of sending to server immediately
    if (historyEditingId && historyColumnTitle.trim()) {
      const newLocalNames = new Map(localColumnNames);
      newLocalNames.set(historyEditingId, historyColumnTitle.trim());
      setLocalColumnNames(newLocalNames);
    }
    updateHistoryEditingId(0);
    updateHistoryColumnTitle("");
  }

  function cancelHistoryEdit(e) {
    updateHistoryEditingId(0);
    updateHistoryColumnTitle("");
  }

  function onTitleChange(e) {
    updateHistoryColumnTitle(e.target.value);
  }

  function handleTitleKeyPressAsync(e) {
    if (e.key === "Enter") saveHistoryEditAsync(e);
  }

  function unselectAllColumns() {
    if (selectedColumnSet && isEditMode) {
      // In edit mode, clear all local checked columns
      setLocalCheckedColumns(new Set());
      setLocalColumnOrder(new Map());
    }
    // If not in edit mode with a column set, there's nothing to unselect
    // since no items should be checked when no column set is selected
  }

  function cancelColumnSetChanges() {
    if (!selectedColumnSet || !originalColumnSetState) return;
    
    // Restore the original state
    const { checkedColumns: originalChecked, columnOrder: originalOrder, columnNames: originalNames } =
      buildLocalStateFromColumnSet(selectedColumnSet);
    
    setLocalCheckedColumns(originalChecked);
    setLocalColumnOrder(originalOrder);
    setLocalColumnNames(originalNames);
  }

  function startColumnSetEdit(columnSet) {
    setColumnSetEditingId(columnSet.id);
    setColumnSetEditingName(columnSet.name);
  }

  function cancelColumnSetEdit() {
    setColumnSetEditingId(0);
    setColumnSetEditingName("");
  }

  async function saveColumnSetEdit() {
    if (!columnSetEditingName.trim()) return;
    
    const putData = { 
      id: columnSetEditingId, 
      name: columnSetEditingName.trim() 
    };
    
    const resp = await RightTimeApi.putAsync(`/HistoryColumnSet/${columnSetEditingId}`, putData);
    if (resp && resp.ok) {
      await getHistoryColumnSets();
      // Update selectedColumnSet if it was the one being edited
      if (selectedColumnSet?.id === columnSetEditingId) {
        const updatedSets = await RightTimeApi.getAsync("/HistoryColumnSets");
        if (updatedSets && updatedSets.ok && updatedSets.jsonData) {
          const updatedColumnSet = updatedSets.jsonData.find(set => set.id === columnSetEditingId);
          if (updatedColumnSet) {
            setSelectedColumnSet(updatedColumnSet);
          }
        }
      }
      toast.success("Column set name updated successfully");
    }
    setColumnSetEditingId(0);
    setColumnSetEditingName("");
  }

  function handleColumnSetNameChange(e) {
    setColumnSetEditingName(e.target.value);
  }

  function handleColumnSetNameKeyPress(e) {
    if (e.key === "Enter") saveColumnSetEdit();
    if (e.key === "Escape") cancelColumnSetEdit();
  }

  function toggleColumnAsync(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));

    // Toggle the column in local state and manage ordering
    if (selectedColumnSet) {
      let newCheckedColumns;
      let newColumnOrder;
      let newColumnNames;

      if (isEditMode) {
        newCheckedColumns = new Set(localCheckedColumns);
        newColumnOrder = new Map(localColumnOrder);
        newColumnNames = new Map(localColumnNames);
      } else {
        const initializedState = buildLocalStateFromColumnSet(selectedColumnSet);
        newCheckedColumns = initializedState.checkedColumns;
        newColumnOrder = initializedState.columnOrder;
        newColumnNames = initializedState.columnNames;
        setIsEditMode(true);
        setOriginalColumnSetState({
          selectedColumns: Array.from(newCheckedColumns)
        });
      }
      
      if (newCheckedColumns.has(id)) {
        // Unchecking - remove from checked, order, and names
        newCheckedColumns.delete(id);
        newColumnOrder.delete(id);
        newColumnNames.delete(id);
      } else {
        // Checking - add to checked and assign next order
        newCheckedColumns.add(id);
        const maxOrder = Math.max(0, ...Array.from(newColumnOrder.values()).map((v) => Number(v)));
        newColumnOrder.set(id, maxOrder + 1);
        
        // Auto-set column name if not already set
        const historyColumn = data.historyColumns?.find(hc => hc.id === id);
        if (historyColumn && !newColumnNames.has(id)) {
          newColumnNames.set(id, historyColumn.name);
        }
      }
      
      setLocalCheckedColumns(newCheckedColumns);
      setLocalColumnOrder(newColumnOrder);
      setLocalColumnNames(newColumnNames);
    }
  }

  function buildLocalStateFromColumnSet(columnSet) {
    const checkedColumns = new Set<number>();
    const columnOrder = new Map<number, number>();
    const columnNames = new Map<number, string>();

    if (columnSet?.columns) {
      columnSet.columns
        .slice()
        .sort((a, b) => a.order - b.order)
        .forEach((col, index) => {
          checkedColumns.add(col.historyColumn.id);
          columnOrder.set(col.historyColumn.id, index + 1);
          columnNames.set(col.historyColumn.id, col.columnName || col.historyColumn.name);
        });
    }

    return { checkedColumns, columnOrder, columnNames };
  }

  useEffect(() => {
    if (props?.tabData?.activeTab === "recents") {
      getAdminColumnsAsync();
      getHistoryColumnSets();
    }
  }, [props?.tabData?.activeTab]);

  // Early returns after all hooks
  if (!props?.tabData || props.tabData.activeTab !== "recents") {
    return null;
  }

  if (!data.historyColumns) {
    return null;
  }

  return (<>
    <div className="tableWrapper dimensionTable columnSetsTable">
      <table className="draggable">
        <caption>
          <span className="title" data-help-id="ColumnSets">Column Sets</span>
          <span className="captionRight">
          <FaPlusSquare  
            className="actionIcon plusIcon" 
            onClick={openNewColumnSetModal}
            title="Create New Column Set"
          />
          </span>
        </caption>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {historyColumnSets.map((columnSet) => (
            <tr 
              key={columnSet.id}
              className={selectedColumnSet?.id === columnSet.id ? "selected active" : ""}
            >
              <td onClick={() => selectColumnSet(columnSet)} className="clickableCell">{columnSet.id}</td>
              <td>
                {columnSetEditingId === columnSet.id ? (
                  <input title="Edit Column Set Name"
                    value={columnSetEditingName}
                    className="inlineEdit"
                    autoFocus
                    onChange={handleColumnSetNameChange}
                    onKeyDown={handleColumnSetNameKeyPress}
                  />
                ) : (
                  <span onClick={() => selectColumnSet(columnSet)} className="left clickableText">{columnSet.name}</span>
                )}
              </td>
              <td>
                {columnSetEditingId === columnSet.id ? (
                  <div className="inlineActionGroup">
                    <RiSave3Fill
                      className="actionIcon saveIcon"
                      onClick={saveColumnSetEdit}
                      title="Save Name"
                    />
                    <ImCancelCircle
                      className="actionIcon cancelIcon"
                      onClick={cancelColumnSetEdit}
                      title="Cancel"
                    />
                  </div>
                ) : isEditMode && selectedColumnSet?.id === columnSet.id && hasChanges() ? (
                  <div className="inlineActionGroup">
                    <RiSave3Fill 
                      className="actionIcon saveIcon" 
                      onClick={saveColumnSetChanges}
                      title="Save Column Set Changes"
                    />
                    <ImCancelCircle
                      className="actionIcon cancelIcon"
                      onClick={cancelColumnSetChanges}
                      title="Cancel Changes"
                    />
                  </div>
                ) : (
                  <div className="inlineActionGroup">
                    <CiEdit
                      className="actionIcon editIcon"
                      onClick={() => startColumnSetEdit(columnSet)}
                      title="Edit Name"
                    />
                    <MdDelete 
                      className="actionIcon trashIcon" 
                      onClick={() => deleteColumnSet(columnSet.id)}
                      title="Delete Column Set"
                    />
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="tableWrapper dimensionTable">
      <table className="draggable">
        <caption>
          <span className="title">{selectedColumnSet ? `Columns for "${selectedColumnSet.name}"` : "Columns — select a column set to edit"}</span>
        </caption>
        <thead>
          <tr>
            <th className="actions">
              {(() => {
                // Determine if any items are currently checked
                const hasCheckedItems = selectedColumnSet && isEditMode 
                  ? localCheckedColumns.size > 0
                  : selectedColumnSet?.columns?.length > 0;
                
                return hasCheckedItems ? (
                  <MdCheckBox className="checkbox checked actionIcon" onClick={unselectAllColumns} title="Unselect All" />
                ) : (
                  <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" onClick={unselectAllColumns} title="Unselect All" />
                );
              })()}
            </th>
            <th>Column</th>
            <th>Displayed Title</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            if (!data.historyColumns) return null;
            
            let columnsToDisplay = [];
            
            if (selectedColumnSet) {
              // When a column set is selected, order columns by the set's order or local order in edit mode
              const setColumnIds = selectedColumnSet.columns.map(col => col.historyColumn.id);
              
              if (isEditMode) {
                // In edit mode, sort checked columns by local order, then unchecked columns
                const checkedColumns = Array.from(localCheckedColumns)
                  .sort((a, b) => (localColumnOrder.get(a) || 0) - (localColumnOrder.get(b) || 0))
                  .map(id => {
                    const historyColumn = data.historyColumns.find(hc => hc.id === id);
                    const setColumn = selectedColumnSet.columns.find(col => col.historyColumn.id === id);
                    return historyColumn ? {
                      historyColumn,
                      setColumn,
                      isInSet: true
                    } : null;
                  })
                  .filter(Boolean);
                
                const uncheckedColumns = data.historyColumns
                  .filter(hc => !localCheckedColumns.has(hc.id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(historyColumn => ({
                    historyColumn,
                    setColumn: null,
                    isInSet: false
                  }));
                
                columnsToDisplay = [...checkedColumns, ...uncheckedColumns];
              } else {
                // Not in edit mode, use original column set order
                selectedColumnSet.columns
                  .sort((a, b) => a.order - b.order)
                  .forEach(setColumn => {
                    const historyColumn = data.historyColumns.find(hc => hc.id === setColumn.historyColumn.id);
                    if (historyColumn) {
                      columnsToDisplay.push({
                        historyColumn,
                        setColumn,
                        isInSet: true
                      });
                    }
                  });
                
                // Then add remaining columns not in the set, sorted alphabetically
                data.historyColumns
                  .filter(hc => !setColumnIds.includes(hc.id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .forEach(historyColumn => {
                    columnsToDisplay.push({
                      historyColumn,
                      setColumn: null,
                      isInSet: false
                    });
                  });
              }
            } else {
              // When no set is selected, show the catalog read-only, sorted alphabetically
              data.historyColumns
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach(historyColumn => {
                  columnsToDisplay.push({
                    historyColumn,
                    setColumn: null,
                    isInSet: false
                  });
                });
            }
            
            return columnsToDisplay.map((item, index) => {
              const { historyColumn, setColumn, isInSet } = item;
              // Use local checked state when in edit mode, otherwise use original logic
              // When no column set is selected, show no items as checked
              const isChecked = (isEditMode && selectedColumnSet) 
                ? localCheckedColumns.has(historyColumn.id)
                : (selectedColumnSet ? isInSet : false);
              
              // Get display title from local names in edit mode, or from set data
              let displayTitle = "";
              if (isEditMode && selectedColumnSet && localColumnNames.has(historyColumn.id)) {
                displayTitle = localColumnNames.get(historyColumn.id);
              } else if (setColumn) {
                displayTitle = setColumn.columnName;
              }
              
              return historyEditingId === historyColumn.id ? (
                <tr key={"rhc-edit-" + historyColumn.id}>
                  <td className="actions">
                    <MdCheckBox
                      className="checkbox checked actionIcon"
                      data-id={historyColumn.id}
                      onClick={toggleColumnAsync}
                    />
                  </td>
                  <td className="left">{historyColumn.name}</td>
                  <td>
                    <input
                      value={historyColumnTitle}
                      className="inlineEdit"
                      aria-label={`Displayed title for ${historyColumn.name}`}
                      placeholder="Enter display title"
                      autoFocus
                      onChange={onTitleChange}
                      onKeyDown={handleTitleKeyPressAsync}
                    />
                  </td>
                  <td className="actions">
                    <RiSave3Fill
                      className="save actionIcon"
                      data-id={historyColumn.id}
                      onClick={saveHistoryEditAsync}
                      title="Save Changes"
                    />
                    <ImCancelCircle
                      className="cancel actionIcon"
                      data-id={historyColumn.id}
                      onClick={cancelHistoryEdit}
                      title="Cancel Changes"
                    />
                  </td>
                </tr>
              ) : (
                <tr 
                  key={"rhc-" + historyColumn.id}
                  draggable={isEditMode && selectedColumnSet && isChecked}
                  onDragStart={(e) => handleDragStart(e, historyColumn.id)}
                  onDragOver={(e) => handleDragOver(e, historyColumn.id)}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  data-help-id="dragHandle"
                  className={`${dropTargetRowId === historyColumn.id && dropPosition === "above"
                      ? "drop-above"
                      : ""
                    } ${dropTargetRowId === historyColumn.id && dropPosition === "below"
                      ? "drop-below"
                      : ""
                    } ${isEditMode && selectedColumnSet && isChecked ? "draggable-row dragHandleRow" : ""}`}
                >
                  <td className="actions">
                    {isChecked ? (
                      <MdCheckBox
                        className="checkbox checked actionIcon"
                        data-id={historyColumn.id}
                        onClick={toggleColumnAsync}
                      />
                    ) : (
                      <MdCheckBoxOutlineBlank
                        className="checkbox unchecked actionIcon"
                        data-id={historyColumn.id}
                        onClick={toggleColumnAsync}
                      />
                    )}
                  </td>
                  <td className="left">{historyColumn.name}</td>
                  <td className="left">{displayTitle}</td>
                  <td className="actions">
                    {historyEditingId === 0 && isChecked ? (
                      <CiEdit
                        className="actionIcon editIcon"
                        data-id={historyColumn.id}
                        onClick={startHistoryEdit}
                        title="Edit"
                      />
                    ) : null}
                  </td>
                </tr>
              );
            });
          })()}
        </tbody>
      </table>

    </div>

    <NewEntityModal
      isOpen={showNewColumnSetModal}
      entityName="Column Set"
      onSave={createNewColumnSet}
      onClose={closeNewColumnSetModal}
      label="Column Set Name"
    />

    <ConfirmModal
      isOpen={showSaveConfirm}
      title="Save Changes"
      message="You have unsaved changes to the current column set. Would you like to save these changes before switching?"
      onConfirm={handleSaveConfirm}
      onCancel={handleCancelSave}
    />

  </>
  );
}
